import {
  memo,
  useState,
  useRef,
  useCallback,
  Children,
  isValidElement,
  useEffect,
  ComponentProps,
  forwardRef,
  ReactNode,
  createContext,
  useContext,
  useId,
  useMemo,
  ReactElement,
  useLayoutEffect,
  CSSProperties,
} from 'react';
import { css } from '@emotion/react';
import {
  useListItem,
  flip,
  size,
  Placement,
  useFloatingNodeId,
  useFloating,
  autoUpdate,
  offset,
  useInteractions,
  useClick,
  useRole,
  useDismiss,
  useListNavigation,
  FloatingNode,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import { Icon, IconName } from '@frontend/icons';
import type { WeaveTheme as OriginalTheme } from '@frontend/theme-original';
import { theme, type WeaveTheme } from '@frontend/theme';
import { useThemeValues } from '../../../../hooks';
import { NakedUl } from '../../../../naked';
import { styles } from '../../../../styles';
import { Button } from '../../../button';
import { Chip } from '../../../chip';
import { Text } from '../../../text';
import { TextLink } from '../../../text-link';
import { useTooltip } from '../../../tooltip/use-tooltip';
import { BaseInputProps, Checkbox, InputProps } from '../../atoms';
import { ListFieldProps, useFormField } from '../../hooks';
import { BasicFormFieldProps, FieldLayout, FieldLayoutWithAction } from '../../layouts';
import { DropdownIcon } from '../dropdown';
import { Menu, SingleSelectionMenu } from './atoms';
import { MAX_FLOATING_HEIGHT, MAX_FLOATING_WIDTH, MIN_FLOATING_WIDTH } from './const';

type CustomInputRenderProps = {
  selectedKeys: string[];
  selectedLabels: string[];
  keyToLabelMap: Record<string, string>;
};
export type CheckListMenuProps = BasicFormFieldProps<'checklist'> & {
  children: ReactNode;
  placement?: Placement;
  customRender?: (args: CustomInputRenderProps) => ReactNode;
  showSearchBar?: boolean;
  searchContainerStyles?: CSSProperties;
  showSelectionBar?: boolean;
  showSelectAll?: boolean;
  floatingStyleOverrides?: React.CSSProperties;
  topContent?: ReactNode;
  customSearchComparator?: (value: string, searchTerm: string) => boolean;
};

export const ChecklistMenuField = ({
  children,
  maxAllowed,
  placeholder = 'Select one',
  placement,
  sortLabelsBySelection = false,
  customRender,
  showSearchBar = true,
  searchContainerStyles,
  showSelectionBar = true,
  showSelectAll = true,
  floatingStyleOverrides,
  topContent,
  customSearchComparator,
  ...rest
}: CheckListMenuProps & {
  sortLabelsBySelection?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const labelsRef = useRef<Array<string | null>>([]);
  const valuesRef = useRef<Array<string | null>>([]);
  const listItemsRef = useRef<Array<HTMLLIElement | null>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const theme = useThemeValues();

  const nodeId = useFloatingNodeId();

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>({
    nodeId,
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: (nextOpen) => {
      setIsOpen(nextOpen);
      if (!nextOpen) {
        setActiveIndex(null);
      }
    },
    middleware: [
      offset(placement === 'top' ? 12 : 3),
      flip(),
      size({
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    placement,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
    useListNavigation(context, {
      activeIndex,
      listRef: listItemsRef,
      loop: true,
      onNavigate: setActiveIndex,
      openOnArrowKeyDown: true,
      selectedIndex,
      /**
       * Using virtual means that focus will stay on the floating element (Menu) even as the user navigates the list - the focus never truly enters the list items.
       * This will change the user experience with keyboard controls like Enter and Space.
       */
      // virtual: true,
    }),
  ]);

  const list = useMemo(
    () =>
      Children.toArray(children).flatMap((child) =>
        isValidElement(child) && child.type === Group ? (child.props.children as ReactNode) : child
      ),
    [children]
  );

  const labelMap = useMemo(() => {
    return Children.toArray(list).reduce((acc, child) => {
      if (isValidElement(child)) {
        acc[child.props.value] = onlyText(child.props.children);
      }
      return acc;
    }, {} as Record<string, string>);
  }, [list]);

  const { sortedKeys, sortedLabels } = useMemo(() => {
    if (sortLabelsBySelection) {
      return {
        sortedKeys: rest.value,
        sortedLabels: rest.value.map((key) => labelMap[key]).filter(Boolean),
      };
    } else {
      const sortedEntries = Object.entries(labelMap)
        .filter(([key]) => rest.value.includes(key))
        .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB));
      return {
        sortedKeys: sortedEntries.map(([key]) => key),
        sortedLabels: sortedEntries.map(([, label]) => label),
      };
    }
  }, [rest.value, labelMap, sortLabelsBySelection]);

  const handleSelect = useCallback(
    (index: number | null) => {
      setSelectedIndex(index);
      if (index !== null) {
        /**
         * Select item by value instead of label to handle duplicate labels correctly.
         */
        const value = valuesRef.current[index];
        if (value !== null && value !== undefined) {
          const item = list.find((child) => isValidElement(child) && child.props?.value === value);

          if (item && isValidElement(item) && !item.props.disabled) {
            rest.onChange({ name: rest.name, value: item.props?.value });
          }
        }
      }
    },
    [list, rest]
  );

  const clearSelection = () => {
    rest.onChange({ name: rest.name, value: [] });
  };

  const selectAll = useCallback(
    (filterFn: (child: ReactElement) => boolean) => {
      const items = Children.toArray(children)
        .filter((child) => isValidElement(child) && !child.props.disabled && filterFn(child))
        .map((child) => (child as ReactElement).props?.value);

      /**
       * Because the multiselect field has special `onChange` logic, this trick allows us to set
       * exactly what we want as the value of the field.
       */
      rest.onChange({ name: rest.name, value: [] });
      rest.onChange({ name: rest.name, value: items });
    },
    [children, rest]
  );

  const labels = useMemo(
    () => sortLabels({ sortLabelsBySelection, values: rest.value, labelMap }),
    [rest.value, labelMap, sortLabelsBySelection]
  );

  useEffect(() => {
    /**
     * This effect is used to update the **label** in the dropdown trigger when the field **value** changes.
     */
    if (labels) {
      setSelectedLabels(labels);
    }
  }, [labels]);

  const selectContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      selectedItems: rest.value,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, getItemProps, rest.value, selectedIndex, handleSelect]
  );

  const customRenderer = useMemo(
    () => (customRender ? CustomInputWrapperField : Field) as ComponentProps<typeof FieldLayoutWithAction>['field'],
    [customRender]
  );

  const fieldComponentProps = useMemo(
    () => ({
      customRender,
      selectedKeys: sortedKeys,
      selectedLabels: sortedLabels,
      keyToLabelMap: labelMap,
      options: children,
      ref: refs.setReference,
    }),
    [customRender, sortedKeys, sortedLabels, labelMap, children, refs.setReference]
  );

  return (
    <FieldLayout
      field={customRenderer}
      fieldComponentProps={fieldComponentProps}
      endAdornment={
        <div
          // Disable pointer events when the dropdown is open so we don't trigger open immediately after an outside click event
          css={{ cursor: 'pointer', pointerEvents: isOpen ? 'none' : 'auto' }}
          onClick={() => {
            setIsOpen((prev) => !prev);
          }}
        >
          <DropdownIcon active={isOpen} />
        </div>
      }
      placeholder={placeholder}
      {...getReferenceProps()}
      {...rest}
      hasPadding={false}
      containerCss={css`
        > div {
          height: 40px;
          padding: ${theme.spacing(1, 1)};
        }
      `}
      /**
       * We derive props below for the field instead of relying on the current state
       */
      value={selectedLabels.join(', ')}
      active={activeIndex !== null || rest.active}
      onChange={() => {}}
    >
      <FloatingNode id={nodeId}>
        <SelectContext.Provider value={selectContext}>
          {isOpen && (
            <FloatingPortal>
              <FloatingFocusManager context={context} initialFocus={0}>
                <div
                  style={{
                    ...floatingStyles,
                    boxShadow: theme.shadows.heavy,
                    borderRadius: theme.borderRadius.medium,
                    backgroundColor: theme.colors.white,
                    zIndex: theme.zIndex.popover,
                    overflow: 'hidden',
                    display: 'grid',
                    gridTemplateRows: 'minmax(0, 1fr)',
                    maxHeight: MAX_FLOATING_HEIGHT,
                    maxWidth: MAX_FLOATING_WIDTH,
                    minWidth: MIN_FLOATING_WIDTH,
                    ...floatingStyleOverrides,
                  }}
                  ref={refs.setFloating}
                >
                  <Menu
                    floatingProps={getFloatingProps({
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSelect(activeIndex);
                        }
                      },
                    })}
                    ref={refs.setFloating}
                    refs={{ listItemRefs: listItemsRef, labelsRef, valuesRef }}
                    clearSelection={clearSelection}
                    selectAll={selectAll}
                    searchContainerStyles={searchContainerStyles}
                    showSearchBar={showSearchBar}
                    showSelectionBar={showSelectionBar}
                    showSelectAll={showSelectAll}
                    topContent={topContent}
                    customSearchComparator={customSearchComparator}
                  >
                    {children}
                  </Menu>
                </div>
              </FloatingFocusManager>
            </FloatingPortal>
          )}
        </SelectContext.Provider>
      </FloatingNode>
    </FieldLayout>
  );
};

const sortLabels = ({
  sortLabelsBySelection,
  values,
  labelMap,
}: {
  sortLabelsBySelection: boolean;
  labelMap: Record<string, string>;
  values: string[];
}) => {
  if (sortLabelsBySelection) {
    /**
     * Loops through selected values first
     */
    return values
      .map((value) => {
        return labelMap[value];
      })
      .filter((label): label is string => typeof label === 'string');
  } else {
    /**
     * Loops through options (children) first
     */
    return Object.keys(labelMap)
      .filter((value) => values.includes(value))
      .map((value) => labelMap[value])
      .filter((label): label is string => typeof label === 'string');
  }
};

export const DropdownFilterChipField = ({
  topContent,
  children,
  maxAllowed,
  placement = 'bottom-start',
  icon,
  label,
  buttonLabel,
  sortLabelsBySelection = false,
  getCustomLabel,
  onApply,
  onDismiss,
  className,
  showClearAll = true,
  isSingleSelect,
  showSelectAll = true,
  filterIsActive,
  ...rest
}: Omit<CheckListMenuProps, 'placeholder'> & {
  icon?: IconName;
  sortLabelsBySelection?: boolean;
  buttonLabel?: string;
  /**
   * Use this to generate a custom label based on the selected items.
   * @param labels list of selected item labels
   * @returns custom label to display in the dropdown trigger
   */
  getCustomLabel?: (labels: string[], labelMap: Record<string, string>) => string;
  onApply?: ({ value, close }: { value: string[]; close: () => void }) => void;
  onDismiss?: ({ value, initialValue }: { value: string[]; initialValue: string[] }) => void;
  showClearAll?: boolean;
  trackingIdBase?: string;
  isSingleSelect?: boolean;
  showSelectAll?: boolean;
  filterIsActive?: boolean;
}) => {
  const theme = useThemeValues();
  const internalField = useFormField({ type: 'checklist', value: rest.value }, [rest.value]);

  return (
    <div css={{ display: 'flex', gap: theme.spacing(2) }}>
      <DropdownFilterChip
        topContent={topContent}
        icon={icon}
        label={label}
        buttonLabel={buttonLabel}
        placement={placement}
        sortLabelsBySelection={sortLabelsBySelection}
        getCustomLabel={getCustomLabel}
        onApply={onApply}
        onDismiss={({ value, initialValue }) => {
          // Reset internal field to what it was before the user opened the dropdown
          internalField.onChange({ name: rest.name, value: [] });
          internalField.onChange({ name: rest.name, value: initialValue });
          onDismiss?.({ value, initialValue });
        }}
        internalField={internalField}
        className={className}
        isSingleSelect={isSingleSelect}
        showSelectAll={showSelectAll}
        filterIsActive={filterIsActive}
        {...rest}
      >
        {children}
      </DropdownFilterChip>
      {!isSingleSelect && (
        <ClearAllButton
          onClick={() => {
            internalField.onChange({ name: 'location-filter', value: [] });
            rest.onChange({ name: 'location-filter', value: [] });
          }}
          // Only show if applied value is not empty
          shouldShow={showClearAll && rest.value.length > 0}
          trackingId={`${rest.trackingIdBase}-clear-all`}
        />
      )}
    </div>
  );
};

/**
 * This component uses an internalField to track changes, and then updates the field value passed in when the user clicks the Apply button.
 */
const DropdownFilterChip = ({
  topContent,
  children,
  placement = 'bottom-start',
  icon,
  label,
  buttonLabel,
  sortLabelsBySelection = false,
  getCustomLabel,
  onApply,
  internalField,
  className,
  onDismiss,
  trackingIdBase,
  isSingleSelect = false,
  showSelectionBar = true,
  showSearchBar = true,
  searchContainerStyles,
  showSelectAll = true,
  filterIsActive,
  ...rest
}: Omit<CheckListMenuProps, 'placeholder'> & {
  internalField: ListFieldProps;
  icon?: IconName;
  sortLabelsBySelection?: boolean;
  buttonLabel?: string;
  /**
   * Use this to generate a custom label based on the selected items.
   * @param labels list of selected item labels
   * @returns custom label to display in the dropdown trigger
   */
  getCustomLabel?: (labels: string[], labelMap: Record<string, string>) => string;
  onApply?: ({ value, close }: { value: string[]; close: () => void }) => void;
  onDismiss?: ({ value, initialValue }: { value: string[]; initialValue: string[] }) => void;
  trackingIdBase?: string;
  isSingleSelect?: boolean;
  showSelectAll?: boolean;
  filterIsActive?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const labelsRef = useRef<Array<string | null>>([]);
  const valuesRef = useRef<Array<string | null>>([]);
  const listItemsRef = useRef<Array<HTMLLIElement | null>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const theme = useThemeValues();

  const nodeId = useFloatingNodeId();

  /**
   * We show an Apply button only if the onApply prop is passed in.
   * In that case, we use an internal field to track changes, and then update the field value passed in when the user clicks the Apply button.
   * If the onApply prop is not passed in, we use the field prop (passed in through rest) directly.
   */
  const field = onApply ? internalField : rest;

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>({
    nodeId,
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: (nextOpen, _e, reason) => {
      setIsOpen(nextOpen);
      if (!nextOpen) {
        setActiveIndex(null);
        if (reason === 'escape-key' || reason === 'outside-press') {
          onDismiss?.({ value: field.value, initialValue: rest.value });
        }
      }
    },
    middleware: [offset(3), flip()],
    placement,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
    useListNavigation(context, {
      activeIndex,
      listRef: listItemsRef,
      loop: true,
      onNavigate: setActiveIndex,
      openOnArrowKeyDown: true,
      selectedIndex,
      /**
       * Using virtual means that focus will stay on the floating element (Menu) even as the user navigates the list - the focus never truly enters the list items.
       * This will change the user experience with keyboard controls like Enter and Space.
       */
      // virtual: true,
    }),
  ]);

  const list = useMemo(
    () =>
      Children.toArray(children).flatMap((child) =>
        isValidElement(child) && child.type === Group ? (child.props.children as ReactNode) : child
      ),
    [children]
  );

  const labelMap = useMemo(() => {
    return Children.toArray(list).reduce((acc, child) => {
      if (isValidElement(child)) {
        acc[child.props.value] = onlyText(child.props.children);
      }
      return acc;
    }, {} as Record<string, string>);
  }, [list]);

  const handleSelect = useCallback(
    (index: number | null) => {
      setSelectedIndex(index);
      if (index !== null) {
        /**
         * Select item by value instead of label to handle duplicate labels correctly.
         */
        const value = valuesRef.current[index];
        if (value !== null && value !== undefined) {
          const item = list.find((child) => isValidElement(child) && child.props?.value === value);

          if (item && isValidElement(item) && !item.props.disabled) {
            if (!('value' in item.props)) {
              return;
            }
            if (isSingleSelect) {
              /**
               * Checklists form fields have logic to find the difference between the incoming and current values.
               * We combine the intended incoming value with the current value to ensure that the fields ends up with only the intended value
               */
              field.onChange({ name: rest.name, value: [item.props?.value, ...field.value] });
              setIsOpen(false);
            } else {
              field.onChange({ name: rest.name, value: item.props?.value });
            }
          }
        }
      }
    },
    [list, field, rest.name, isSingleSelect]
  );

  const clearSelection = () => {
    field.onChange({ name: rest.name, value: [] });
  };

  const selectAll = useCallback(
    (filterFn: (child: ReactElement) => boolean) => {
      const items = Children.toArray(children)
        .filter((child) => isValidElement(child) && !child.props.disabled && filterFn(child))
        .map((child) => (child as ReactElement).props?.value);

      /**
       * Because the multiselect field has special `onChange` logic, this trick allows us to set
       * exactly what we want as the value of the field.
       */
      field.onChange({ name: rest.name, value: [] });
      field.onChange({ name: rest.name, value: items });
    },
    [children, field, rest.name]
  );
  const labels = useMemo(
    () => sortLabels({ sortLabelsBySelection, values: rest.value, labelMap }),
    [rest.value, labelMap, sortLabelsBySelection]
  );

  useEffect(() => {
    /**
     * This effect is used to update the **label** in the dropdown trigger when the field **value** changes.
     */
    if (labels) {
      setSelectedLabels(labels);
    }
  }, [labels]);

  const selectContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      selectedItems: field.value,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, getItemProps, field.value, selectedIndex, handleSelect]
  );

  return (
    <>
      <Chip.DropdownFilter
        {...getReferenceProps()}
        filterIsActive={filterIsActive}
        leftElement={icon ? <Icon name={icon} size={16} /> : null}
        ref={refs.setReference}
        isOpen={isOpen}
        css={{ maxWidth: 240 }}
        className={className}
        trackingId={trackingIdBase}
      >
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Text size='large' css={styles.truncate} color={filterIsActive ? 'white' : 'default'}>
            {getCustomLabel && selectedLabels.length > 0 ? getCustomLabel(selectedLabels, labelMap) : label}
          </Text>
        </div>
      </Chip.DropdownFilter>
      <FloatingNode id={nodeId}>
        <SelectContext.Provider value={selectContext}>
          <AnimatePresence>
            {isOpen && (
              <FloatingPortal>
                <FloatingFocusManager context={context} initialFocus={0}>
                  <motion.div
                    style={{
                      ...floatingStyles,
                      zIndex: theme.zIndex.popover,
                    }}
                    ref={refs.setFloating}
                  >
                    <motion.div
                      initial='closed'
                      animate={isOpen ? 'open' : 'closed'}
                      variants={{
                        open: { opacity: 1, transform: 'translateY(8px)' },
                        closed: { opacity: 0, transform: 'translateY(0)' },
                      }}
                      exit='closed'
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      style={{
                        boxShadow: theme.shadows.heavy,
                        borderRadius: theme.borderRadius.medium,
                        backgroundColor: theme.colors.white,
                        overflow: 'hidden',

                        display: 'grid',
                        gridTemplateRows: 'minmax(0, 1fr) auto',
                        maxHeight: MAX_FLOATING_HEIGHT,
                        maxWidth: MAX_FLOATING_WIDTH,
                        minWidth: MIN_FLOATING_WIDTH,
                      }}
                    >
                      {isSingleSelect ? (
                        <SingleSelectionMenu
                          floatingProps={getFloatingProps({
                            onKeyDown: (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handleSelect(activeIndex);
                              }
                            },
                          })}
                          showSelectionBar={showSelectionBar}
                          showSearchBar={showSearchBar}
                          refs={{ listItemRefs: listItemsRef, labelsRef, valuesRef }}
                          clearSelection={clearSelection}
                          trackingIdBase={trackingIdBase}
                        >
                          {children}
                        </SingleSelectionMenu>
                      ) : (
                        <>
                          <Menu
                            topContent={topContent}
                            floatingProps={getFloatingProps({
                              onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleSelect(activeIndex);
                                }
                              },
                            })}
                            showSelectionBar={showSelectionBar}
                            showSearchBar={showSearchBar}
                            searchContainerStyles={searchContainerStyles}
                            refs={{ listItemRefs: listItemsRef, labelsRef, valuesRef }}
                            clearSelection={clearSelection}
                            selectAll={selectAll}
                            trackingIdBase={trackingIdBase}
                            showSelectAll={showSelectAll}
                          >
                            {children}
                          </Menu>
                          {onApply && (
                            <div style={{ padding: theme.spacing(1, 2, 2) }}>
                              <Button
                                onClick={() => {
                                  rest.onChange({ name: rest.name, value: [] });
                                  rest.onChange({ name: rest.name, value: field.value });

                                  onApply({ value: field.value, close: () => setIsOpen(false) });
                                }}
                                trackingId={`${trackingIdBase}-apply-button`}
                              >
                                {buttonLabel ?? 'Apply'}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  </motion.div>
                </FloatingFocusManager>
              </FloatingPortal>
            )}
          </AnimatePresence>
        </SelectContext.Provider>
      </FloatingNode>
    </>
  );
};

const Field = forwardRef<HTMLInputElement>((props, ref) => {
  const theme = useThemeValues();
  return (
    <input
      style={{ cursor: 'default', padding: theme.spacing(0, 4, 0, 2), height: '100%', margin: 0 }}
      css={styles.truncate}
      readOnly
      ref={ref}
      {...props}
    />
  );
});

Field.displayName = 'Field';

type CustomInputWrapperFieldProps = BaseInputProps &
  InputProps<'text'> & {
    selectedKeys: string[];
    selectedLabels: string[];
    keyToLabelMap: Record<string, string>;
    customRender?: (propsValue: CustomInputRenderProps) => ReactNode;
  };

const CustomInputWrapperField = memo(
  forwardRef<HTMLInputElement, CustomInputWrapperFieldProps>(
    ({ customRender, selectedKeys, selectedLabels, keyToLabelMap, ...props }, ref) => {
      const theme = useThemeValues();
      const renderProps = useMemo(
        () => ({ selectedKeys, selectedLabels, keyToLabelMap }),
        [selectedKeys, selectedLabels, keyToLabelMap]
      );
      return (
        <div ref={ref} {...props} style={{ width: '100%', height: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            {customRender ? (
              customRender(renderProps)
            ) : (
              <input
                style={{ flexGrow: 1, flexBasis: 'min-content', padding: theme.spacing(0, 4, 0, 2) }}
                readOnly
                {...props}
                type={selectedKeys.length > 0 ? 'hidden' : 'text'}
              />
            )}
          </div>
        </div>
      );
    }
  )
);

CustomInputWrapperField.displayName = 'CustomInputWrapperField';

type SelectProps = {
  activeIndex: number | null;
  selectedIndex: number | null;
  selectedItems: string[];
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  handleSelect: (index: number | null) => void;
};

export const SelectContext = createContext({} as SelectProps);

const Option = ({
  children,
  value,
  disabled,
  trackingId,
  disabledMessage,
  className,
}: {
  children: ReactNode;
  value: string;
  disabled?: boolean;
  trackingId?: string;
  disabledMessage?: string;
  className?: string;
}) => {
  const {
    Tooltip,
    tooltipProps,
    triggerProps: { ref: tooltipRef, ...triggerProps },
  } = useTooltip({
    trigger: 'hover',
  });
  const { activeIndex, selectedItems, getItemProps, handleSelect } = useContext(SelectContext);
  const theme = useThemeValues();
  const id = useId();

  const { ref, index } = useListItem();
  const isActive = activeIndex === index;
  const isSelected = selectedItems.includes(value);

  return (
    <li
      aria-disabled={disabled}
      ref={(node) => {
        ref(node);
        tooltipRef(node);
      }}
      id={id}
      role='option'
      aria-selected={isActive && isSelected}
      tabIndex={-1}
      css={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: isActive ? (theme as WeaveTheme).colors.neutral5 ?? (theme as OriginalTheme).colors.gray200 : '',
        listStyle: 'none',
        display: 'flex',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        outline: 'none',
        color: disabled
          ? theme.font.colors.disabled
          : (theme as WeaveTheme).colors.neutral90 ?? (theme as OriginalTheme).colors.gray600,
      }}
      className={className}
      {...triggerProps}
      {...getItemProps({
        onClick: () => {
          handleSelect(index);
        },
      })}
    >
      <Checkbox
        disabled={disabled}
        name='test'
        id={`checkbox-${id}`}
        aria-invalid={false}
        value={isSelected}
        onBlur={() => {}}
        onChange={() => {}}
        onFocus={() => {}}
        active={isActive}
        error={false}
        tabIndex={-1}
        trackingId={trackingId}
      />
      {children}
      {disabled && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {disabledMessage ?? 'This option is disabled'}
          </Text>
        </Tooltip>
      )}
    </li>
  );
};

Option.displayName = 'Option';

const Group = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <div
      style={{
        listStyle: 'none',
      }}
    >
      <Text
        css={{
          padding: theme.spacing(0, 2),
        }}
        weight='bold'
      >
        {label}
      </Text>
      <NakedUl>{children}</NakedUl>
    </div>
  );
};

Group.displayName = 'Group';

const ClearAllButton = ({
  label,
  onClick,
  shouldShow,
  trackingId,
}: {
  label?: string;
  onClick: () => void;
  shouldShow?: boolean;
  trackingId?: string;
}) => {
  return (
    <TextLink
      hidden={!shouldShow}
      weight='bold'
      onKeyDown={(e) => {
        if (e.key === ' ') {
          e.preventDefault();
        }
      }}
      onClick={onClick}
      trackingId={trackingId}
    >
      {label ?? 'Clear All'}
    </TextLink>
  );
};

const SingleSelectOption = ({
  children,
  value,
  disabled,
  trackingId,
}: {
  children: ReactNode;
  value: string;
  disabled?: boolean;
  trackingId?: string;
}) => {
  const { activeIndex, selectedItems, getItemProps, handleSelect } = useContext(SelectContext);
  const theme = useThemeValues();
  const id = useId();

  const { ref, index } = useListItem();
  const isActive = activeIndex === index;
  const isSelected = selectedItems.includes(value);

  return (
    <li
      aria-disabled={disabled}
      ref={ref}
      id={id}
      role='option'
      aria-selected={isActive && isSelected}
      tabIndex={-1}
      css={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: isActive ? (theme as WeaveTheme).colors.neutral5 ?? (theme as OriginalTheme).colors.gray200 : '',
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: '24px 1fr',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        outline: 'none',
        color: disabled
          ? theme.font.colors.disabled
          : (theme as WeaveTheme).colors.neutral90 ?? (theme as OriginalTheme).colors.gray600,
      }}
      data-trackingId={trackingId}
      {...getItemProps({
        onClick: () => {
          handleSelect(index);
        },
      })}
    >
      {isSelected && <Icon name='check' color='primary' />}
      {typeof children === 'string' ? (
        <Text css={{ gridColumn: 2 }}>{children}</Text>
      ) : (
        <div style={{ gridColumn: 2 }}>{children}</div>
      )}
    </li>
  );
};

export const ChipGroupInput = ({
  selectedKeys,
  onRemoveClick,
  keyToLabelMap,
  variant,
  showOverflowTooltip = false,
}: {
  selectedKeys: string[];
  onRemoveClick: (id: string) => void;
  keyToLabelMap: Record<string, string>;
  showOverflowTooltip?: boolean;
  variant?:
    | 'primary'
    | 'critical'
    | 'success'
    | 'warn'
    | 'active'
    | 'eggplant'
    | 'seaweed'
    | 'disabled'
    | 'interactive'
    | 'neutral'
    | 'outline'
    | 'default'
    | 'primaryDark'
    | 'warningDark'
    | 'warningNeutral'
    | 'indigo'
    | 'tangerine'
    | 'rose';
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(selectedKeys);
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  const updateVisibleCount = useCallback(() => {
    if (!containerRef.current || !hiddenContainerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;

    // Don't proceed if container doesn't have a width yet
    if (containerWidth === 0) return;

    const chipWidths = Array.from(hiddenContainerRef.current.children).map(
      (child) => (child as HTMLElement).offsetWidth
    );

    // First try to fit all chips without reserving extra space
    let totalWidth = 0;
    let count = 0;

    for (const width of chipWidths) {
      totalWidth += width;
      count++;

      // If we've exceeded the container width, back up one
      if (totalWidth > containerWidth) {
        count--;
        break;
      }
    }

    // If we couldn't fit all chips, then we need to reserve space for the "+X more" chip
    if (count < selectedKeys.length) {
      const reservedWidthForNewChip = 80; // Approximate width for a new chip
      const availableWidth = containerWidth - reservedWidthForNewChip;

      totalWidth = 0;
      count = 0;

      for (const width of chipWidths) {
        if (totalWidth + width > availableWidth) break;
        totalWidth += width;
        count++;
      }
      setVisibleKeys(selectedKeys.slice(0, Math.max(count, 1)));
    } else {
      setVisibleKeys(selectedKeys);
    }
  }, [selectedKeys]);

  useLayoutEffect(() => {
    if (containerRef.current?.offsetWidth === 0) {
      // If the container width is 0 (during initial render), wait for the next render cycle to measure it
      setTimeout(() => {
        updateVisibleCount();
      }, 0);
    } else {
      updateVisibleCount();
    }
  }, [updateVisibleCount]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(0, max-content))',
          gap: theme.spacing(1),
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: 'calc(100% - 8px)',
          cursor: 'pointer',
        }}
      >
        {visibleKeys.map((value) => {
          return (
            <Chip.Removable
              variant={variant}
              css={{ width: 'fit-content', maxWidth: 'fit-content' }}
              className='chip-to-measure'
              key={value}
              onClick={(e) => {
                e.stopPropagation();
                if (value !== undefined) {
                  onRemoveClick(value);
                }
              }}
            >
              {keyToLabelMap[value] ?? value}
            </Chip.Removable>
          );
        })}
        {visibleKeys.length < selectedKeys.length && (
          <span
            {...(showOverflowTooltip ? triggerProps : {})}
            style={{ maxWidth: 70, justifySelf: 'end', gridColumn: '-1', cursor: 'default' }}
          >
            <Text color='subdued'>+{selectedKeys.length - visibleKeys.length} more</Text>
          </span>
        )}
      </div>
      {showOverflowTooltip && (
        <Tooltip {...tooltipProps}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
            {selectedKeys.slice(visibleKeys.length).map((key) => (
              <Text key={key} size='medium' color='white'>
                {keyToLabelMap[key] ?? key}
              </Text>
            ))}
          </div>
        </Tooltip>
      )}
      <div
        ref={hiddenContainerRef}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(0, max-content))',
          gap: theme.spacing(1),
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: 'calc(100% - 8px)',
          visibility: 'hidden',
        }}
      >
        {/* These chips have to have the exact same style properties as the one above. This is used as a basis for measurement */}
        {selectedKeys.map((value) => {
          return (
            <Chip.Removable
              variant={variant}
              css={{ width: 'fit-content', maxWidth: 'fit-content' }}
              key={value}
              onClick={(e) => {
                e.stopPropagation();
                if (value !== undefined) {
                  onRemoveClick(value);
                }
              }}
            >
              {keyToLabelMap[value] ?? value}
            </Chip.Removable>
          );
        })}
      </div>
    </>
  );
};

SingleSelectOption.displayName = 'SingleSelectOption';

ChecklistMenuField.Option = Option;
ChecklistMenuField.Group = Group;
ChecklistMenuField.ChipInput = ChipGroupInput;
DropdownFilterChipField.Option = Option;
DropdownFilterChipField.Group = Group;
DropdownFilterChipField.SingleSelectOption = SingleSelectOption;
DropdownFilterChipField.ChipInput = ChipGroupInput;
